// Define possible registration statuses
const statuses = {
    confirmed: 'OPT_IN_CONFIRMED', // User has opted in and confirmed
    declined: 'OPT_OUT_CONFIRMED', // User has opted out and confirmed
    pending: 'OPT_IN_PENDING',     // User has initiated opt-in but not yet confirmed
};

export default function useAlertSmsManager() {
    const application = 'COMMODITY_TRENDS'; // Application identifier for the SMS alerts system
    const environment = 'forProduction'; // Environment setting: forProduction, forStaging
    const transport = Barchart.Engagement.Transport.SMS; // Define the transport type as SMS

    // JWT provider to get the authentication token, refreshed every 5 minutes
    let jwtProvider = new Barchart.Engagement.JwtProvider(async () => {
        // Fetch a new JWT token from the server
        const response = await axios.post(window.route('api.generate-jwt-engagement'));
        return response.data.toString();
    }, 5 * 60 * 1000);

    // Variables to hold the promise of the gateway connection and the gateway itself
    let gatewayPromise = null;
    let engagementGateway = null;
    let subscription = null; // Holds the current subscription to registration updates

    // Connect to the engagement gateway, ensuring a single connection instance
    const connect = async () => {
        if (!gatewayPromise) {
            // Initialize the gateway connection using the provided environment, JWT provider, and application
            gatewayPromise = Barchart.Engagement.EngagementGateway[environment](jwtProvider, application);
        }
        engagementGateway = await gatewayPromise;
        return engagementGateway;
    };

    /**
     * Validates an international phone number.
     * @param {string} phone - The phone number to validate.
     * @returns {boolean|string} - Returns the formatted phone number if valid, or false if invalid.
     */
    const validatePhoneNumber = async (phone) => {
        try {
            // Ensure gateway connection
            await connect();

            const RemoteType = require('@barchart/engagement-client-js/lib/data/RemoteType');
            const query = {
                type: RemoteType.parse('PHONE_NUMBER'),
                address: phone
            };

            // Perform the validation using the engagement gateway
            const validationResponse = await engagementGateway.validateRemote(query);

            // Parse the response string into a JSON object
            const validation = JSON.parse(validationResponse);

            // Check if the validation was successful
            if (validation.success) {
                // Return the formatted phone number if available, otherwise the original
                return Promise.resolve(validation.formatted || validation.original);
            } else {
                // Return false if the validation was not successful
                return Promise.resolve(false);
            }
        } catch (error) {
            console.error('Exception occurred during validation:', error);
            return Promise.resolve(false);
        }
    };

    /**
     * Reads the registration status of a phone number.
     * @param {string} phone - The phone number to check.
     * @returns {object|boolean} - Returns the registration object or false if an error occurs.
     */
    const readRegistration = async (phone) => {
        // Ensure gateway connection
        await connect();

        const query = {
            application: application,
            transport: transport,
            target: phone,
        };

        try {
            // Retrieve the registration status
            const registration = await engagementGateway.readRegistration(query);
            return registration;
        } catch (error) {
            console.error('Failed to read registration:', error);
            return false;
        }
    };

    /**
     * Creates a new registration for a phone number.
     * @param {string} phone - The phone number to register.
     * @returns {object|boolean} - Returns the registration object or false if an error occurs.
     */
    const createRegistration = async (phone) => {
        // Ensure gateway connection
        await connect();

        // Define the registration details
        const registration = {
            application: application,
            transport: transport,
            target: phone,
            status: Barchart.Engagement.RegistrationStatus.parse(statuses.pending),
        };

        // Create the registration
        const registered = await engagementGateway.createRegistration(registration, true);
        return registered;
    };

    /**
     * Subscribes to updates for a phone number's registration status.
     * @param {string} phone - The phone number to subscribe to.
     * @param {function} callback - A callback function to handle registration updates.
     * @returns {object|boolean} - Returns the subscription object or false if an error occurs.
     */
    const subscribeRegistration = async (phone, callback) => {
        // Ensure gateway connection
        await connect();

        // Clean up existing subscription if it exists
        if (subscription) {
            // Dispose of the current subscription
            unsubscribeRegistration();
        }

        const query = {
            application: application,
            transport: transport,
            target: phone,
        };

        // Subscribe to registration updates and handle them with the provided callback
        subscription = await engagementGateway.subscribeRegistration(query, (registration) => {
            try {
                if (callback) {
                    // Invoke the callback with the updated registration
                    callback(registration);
                }
            } catch (error) {
                console.error('Error in subscription callback:', error);
                return false;
            }
        });

        // Return the subscription object
        return subscription;
    };

    /**
     * Unsubscribes from the current registration updates.
     */
    const unsubscribeRegistration = () => {
        if (subscription) {
            // Dispose of the current subscription
            subscription.dispose();
            subscription = null;
        }
    };

    // Return the functions and statuses for use elsewhere in the application
    return {
        statuses,
        validatePhoneNumber,
        readRegistration,
        createRegistration,
        subscribeRegistration,
        unsubscribeRegistration,
    };
}
